import { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, FileText, ClipboardList, PenTool } from 'lucide-react';
import ZenButton from '../components/ZenButton';
import SuperAdminMode from '../components/SuperAdminMode';
import { AccesRapide } from '../components/dashboard-centre/AccesRapide';
import { ATraiter } from '../components/dashboard-centre/ATraiter';
import { TuileGuide } from '../components/guide-bc/TuileGuide';

const DashboardCentre = ({ user, onImpersonate }) => {
  const [stats, setStats] = useState({
    stagiaires: 0,
    formateurs: 0,
    tuteurs: 0,
    notifications: 0,
    entreprises: 0,
    demandesAbsenceEnAttente: 0
  });
  const [kpis, setKpis] = useState(null);
  const [stagiaires, setStagiaires] = useState([]);
  const [demandesAbsence, setDemandesAbsence] = useState([]);
  const [convAttente] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, []);

  const loadData = async (signal) => {
    try {
      // ENDPOINT AGRÉGÉ: 1 requête au lieu de 7+ séquentielles
      const dashRes = await axios.get('/dashboard/centre-data', { signal }).catch(() => ({ data: {} }));
      const dashData = dashRes.data || {};
      
      const stagiairesList = (dashData.stagiaires || []).filter(s => s.statut_validation !== 'supprime' && s.is_active !== false);
      setStagiaires(stagiairesList);
      setDemandesAbsence(dashData.demandes_absence || []);
      const notifNonLues = (dashData.notifications || []).filter(n => !n.lu);

      const demandesEnAttente = (dashData.demandes_absence || []).filter(d => d.statut === 'en_attente').length;

      setStats({
        stagiaires: stagiairesList.length,
        formateurs: (dashData.formateurs || []).length,
        tuteurs: (dashData.tuteurs || []).length,
        entreprises: (dashData.entreprises || []).length,
        notifications: notifNonLues.length,
        demandesAbsenceEnAttente: demandesEnAttente
      });

      // KPIs en 2ème requête séparée (optionnel, ne bloque pas l'affichage)
      try {
        const kpisRes = await axios.get('/finances/kpis-centre');
        if (kpisRes.data) setKpis(kpisRes.data);
      } catch (e) { /* kpis non disponibles */ }

    } catch (error) {
    }
  };

  // Grouper les stagiaires par centre - seulement Atlantique et Occitanie
  const stagiairesByCentre = {
    Atlantique: stagiaires.filter(s => {
      const c = (s.centre_formation || s.centre || '').toLowerCase();
      return c === 'atlantique';
    }),
    Occitanie: stagiaires.filter(s => {
      const c = (s.centre_formation || s.centre || '').toLowerCase();
      return c === 'occitanie';
    })
  };

  return (
    <div className="space-y-8" data-testid="dashboard-centre">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-[#0F172A] mb-2">Tableau de Bord</h1>
          <p className="text-slate-600">Centre de Formation - Vue d'ensemble</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SuperAdminMode onImpersonate={onImpersonate} />
          <a href="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000051901186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300" data-testid="referentiel-diplome-btn">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
            <span className="hidden sm:inline">Réf.</span>
          </a>
          <ZenButton showCompass={false} />
        </div>
      </div>

      {/* 1. BANNIERE Conventions en attente */}
      {convAttente.length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-5 text-white shadow-lg" data-testid="banner-convention-centre">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-full p-3">
              <PenTool className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold">{convAttente.length} Convention(s) en attente</h3>
              <div className="flex flex-wrap gap-3 mt-2">
                {convAttente.map(c => (
                  <div key={c.stagiaire_id} className="bg-white/15 rounded-lg px-3 py-1.5 text-sm">
                    <span className="font-semibold">{c.stagiaire_prenom} {c.stagiaire_nom}</span>
                    <span className="ml-2 text-white/80">
                      {c.statut === 'en_attente_validation_centre' ? '(Toutes signatures — à valider)' :
                       `(${[!c.signature_stagiaire && 'Stagiaire', !c.signature_tuteur && 'Tuteur', !c.signature_entreprise && 'Entreprise'].filter(Boolean).join(', ')} manquant${[!c.signature_stagiaire, !c.signature_tuteur, !c.signature_entreprise].filter(Boolean).length > 1 ? 's' : ''})`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. KPIs en temps réel */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="kpis-centre">
          {/* Taux de présence */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Taux de présence</span>
              <span className="text-xs text-slate-400">7 derniers jours</span>
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-3xl font-bold ${kpis.taux_presence >= 90 ? 'text-green-600' : kpis.taux_presence >= 70 ? 'text-amber-600' : 'text-red-600'}`} data-testid="kpi-taux-presence">
                {kpis.taux_presence}%
              </span>
            </div>
            <div className="mt-2 w-full bg-slate-100 rounded-full h-2">
              <div className={`h-2 rounded-full ${kpis.taux_presence >= 90 ? 'bg-green-500' : kpis.taux_presence >= 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${kpis.taux_presence}%` }} />
            </div>
          </div>

          {/* Grilles non remplies */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Grilles non remplies</span>
              <ClipboardList className="h-4 w-4 text-amber-500" />
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-3xl font-bold ${kpis.grilles_vides === 0 ? 'text-green-600' : 'text-amber-600'}`} data-testid="kpi-grilles-vides">
                {kpis.grilles_vides}
              </span>
              <span className="text-sm text-slate-400 mb-1">/ {kpis.total_stagiaires}</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">stagiaires sans évaluation</p>
          </div>

          {/* Paiements en retard */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Paiements en retard</span>
              <AlertTriangle className={`h-4 w-4 ${kpis.paiements_retard > 0 ? 'text-red-500' : 'text-green-500'}`} />
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-3xl font-bold ${kpis.paiements_retard === 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="kpi-paiements-retard">
                {kpis.paiements_retard}
              </span>
            </div>
            {kpis.montant_retard > 0 && (
              <p className="text-xs text-red-500 mt-1 font-medium">{kpis.montant_retard.toLocaleString('fr-FR')} € en attente</p>
            )}
            {kpis.paiements_retard === 0 && (
              <p className="text-xs text-green-500 mt-1">Tous les paiements sont à jour</p>
            )}
          </div>

          {/* Documents manquants */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Documents manquants</span>
              <FileText className={`h-4 w-4 ${kpis.docs_manquants > 0 ? 'text-orange-500' : 'text-green-500'}`} />
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-3xl font-bold ${kpis.docs_manquants === 0 ? 'text-green-600' : 'text-orange-600'}`} data-testid="kpi-docs-manquants">
                {kpis.docs_manquants}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">pièces à fournir</p>
          </div>
        </div>
      )}

      <TuileGuide />

      {/* 3. Accès rapide (Stagiaires, Business…) */}
      <AccesRapide stats={stats} parCentre={{ Atlantique: stagiairesByCentre.Atlantique.length, Occitanie: stagiairesByCentre.Occitanie.length }} />

      {/* 4. À traiter : tuiles dépliables */}
      <ATraiter user={user} stagiaires={stagiaires} demandesAbsence={demandesAbsence} />
    </div>
  );
};

export default DashboardCentre;
